import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

/* ------------------ SHAKE ANIMATION ------------------ */
const shakeAnim = {
  x: [0, -8, 8, -6, 6, 0],
};

/* ------------------ COMPONENT ------------------ */
const ServiceCard = ({ service }) => {
  const Icon = service.icon;

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [shake, setShake] = useState(false);

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* ------------------ VALIDATION ------------------ */
  const validateStep1 = () => {
    if (!form.name || !form.phone) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.message) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return false;
    }
    return true;
  };

  /* ------------------ WHATSAPP LINK ------------------ */
  const whatsappLink = () => {
    const text = `
Service Quote Request

Service: ${service.title}

Name: ${form.name}
Company: ${form.company || "-"}
Email: ${form.email || "-"}
Phone: ${form.phone}

Message:
${form.message}
    `.trim();

    return `https://wa.me/916382585971?text=${encodeURIComponent(text)}`;
  };

  return (
    <>
      {/* ================= CARD ================= */}
      <Card
        sx={{
          height: "100%",
          p: 4,
          borderRadius: 4,
          background:
            "linear-gradient(145deg, rgba(15,25,60,0.9), rgba(10,17,40,0.95))",
          border: "1px solid rgba(255,77,77,0.35)",
          textAlign: "center",
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            mx: "auto",
            mb: 3,
            borderRadius: "22%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(145deg, rgba(255,77,77,0.25), rgba(139,0,0,0.4))",
          }}
        >
          <Icon sx={{ fontSize: 42, color: "#ff4d4d" }} />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            mb: 2,
            background: "linear-gradient(45deg, #ff4d4d, #ff8080)",
            WebkitBackgroundClip: "text",
            color: "transparent",
          }}
        >
          {service.title}
        </Typography>

        <Typography
          sx={{
            color: "rgba(255,255,255,0.85)",
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          {service.description}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mb: 3 }}>
          {service.features.slice(0, 3).map((f, i) => (
            <Chip
              key={i}
              label={f}
              size="small"
              sx={{
                color: "#fff",
                background: "rgba(255,77,77,0.25)",
              }}
            />
          ))}
        </Box>

        <Button
          fullWidth
          onClick={() => {
            setOpen(true);
            setStep(1);
          }}
          endIcon={<RocketLaunchIcon />}
          sx={{
            fontWeight: 800,
            py: 1.3,
            borderRadius: 3,
            color: "#fff",
            background: "linear-gradient(135deg, #ff4d4d, #8b0000)",
          }}
        >
          Get Quote
        </Button>
      </Card>

      {/* ================= DIALOG ================= */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 900,
            color: "#ff4d4d",
          }}
        >
          {service.title} – Quote
        </DialogTitle>

        <DialogContent>
          <motion.div animate={shake ? shakeAnim : {}}>
            {/* -------- STEP 1 -------- */}
            {step === 1 && (
              <Stack spacing={2} mt={2}>
                <TextField
                  name="name"
                  label="Name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  fullWidth
                />
                <TextField
                  name="company"
                  label="Company"
                  value={form.company}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  name="email"
                  label="Email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  name="phone"
                  label="Phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  fullWidth
                />

                <Button
                  onClick={() => validateStep1() && setStep(2)}
                  sx={{
                    py: 1.3,
                    fontWeight: 800,
                    color: "#fff",
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #ff4d4d, #8b0000)",
                  }}
                >
                  Next
                </Button>
              </Stack>
            )}

            {/* -------- STEP 2 -------- */}
            {step === 2 && (
              <Stack spacing={2} mt={2}>
                <TextField
                  name="message"
                  label="Message"
                  value={form.message}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  required
                  fullWidth
                />

                <Button
                  component="a"
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (!validateStep2()) {
                      e.preventDefault();
                      return;
                    }
                    setOpen(false);
                  }}
                  sx={{
                    py: 1.4,
                    fontWeight: 900,
                    color: "#fff",
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #ff4d4d, #8b0000)",
                  }}
                >
                  Send via WhatsApp
                </Button>
              </Stack>
            )}
          </motion.div>
        </DialogContent>
      </Dialog>
      
    </>
  );
};

export default ServiceCard;
